import type { EdgeUser } from "@/types/api";

/**
 * Shared visibility helpers for end-users. Used by both the map marker layer
 * and the widget list so the two always render the same set.
 */

export type EndUserVisibilityContext = {
    showLayers: readonly string[] | undefined;
    exposureStringFromLevel: (level: number | undefined) => string;
};

/** Master `endusers` toggle, or this user's specific `enduser-<value>` toggle, is on. */
export function isExposureLayerVisible(
    exposureLevel: number | undefined,
    showLayers: readonly string[] | undefined,
    exposureStringFromLevel: (level: number | undefined) => string,
): boolean {
    if (!showLayers) return false;
    if (showLayers.includes("endusers")) return true;
    const exposureString = exposureLevel ? exposureStringFromLevel(exposureLevel) : "";
    return !!exposureString && showLayers.includes(`enduser-${exposureString}`);
}

/**
 * Single eligibility predicate for map marker placement AND widget list membership.
 * Keeping this in one place guarantees "user on map" ≡ "user in widget list".
 *
 * A user is shown when ALL of the following hold:
 *
 *  1. Coordinates — lat and lng are both non-null. Without a position the user
 *     cannot be placed on the map.
 *
 *  2. Last location post — lastLocationPost is non-null. The user must have
 *     posted at least one location update. Users who have never reported a
 *     location (e.g. app not yet opened) are hidden entirely.
 *     Note: staleness is NOT checked — a user stays visible regardless of how
 *     long ago they last reported, as long as lastLocationPost is present.
 *
 *  3. Exposure layer toggle — the operator's layer panel must have the user's
 *     exposure level enabled. Either the master "endusers" toggle is on, or the
 *     per-level "enduser-<colour>" toggle is on for this user's exposure level.
 *     Users with no exposure level (undefined) are treated as "safe" for this
 *     check.
 *
 * Marker appearance (colour, level-dimming) is determined separately:
 *  - Colour is driven solely by exposureLevel → CSS class enduser-color-<colour>.
 *  - Markers on the currently selected floor render at full opacity.
 *  - Markers on other floors render at 30% opacity via .marker-other-level.
 *  - Name is NOT checked — anonymous users appear on the map and are counted
 *    separately in the widget header.
 */
export function isEndUserVisible(
    user: EdgeUser,
    ctx: EndUserVisibilityContext,
): boolean {
    if (user.lat == null || user.lng == null) return false;
    if (!user.lastLocationPost) return false;
    return isExposureLayerVisible(user.exposureLevel, ctx.showLayers, ctx.exposureStringFromLevel);
}

export function endUserHasName(user: Partial<EdgeUser>): boolean {
    return !!(user.firstName || user.lastName || user.displayName);
}

/**
 * Firstname + lastname when either exists, else the displayName. Shared by the
 * personnel widget and the map marker tooltips so the two never label the same
 * person differently.
 */
export function getEndUserDisplayName(
    user: Partial<EdgeUser> | null | undefined,
): string {
    if (!user) return "";

    if (user.firstName || user.lastName) {
        return `${user.firstName || ""} ${user.lastName || ""}`.trim();
    }

    return user.displayName || "";
}

/**
 * Render gate: a user may be shown once they have a key or a name. MQTT
 * location deltas create a location stub and queue a fetchUserById; a
 * completely anonymous stub must not render or be counted.
 *
 * Note this is deliberately weaker than the store's fetch gate
 * (venueData.applyDelta): a stub whose MQTT delta carried a name renders
 * immediately, but it still lacks the API-only fields (`wearable` drives the
 * device-type icon), so the store keeps fetching until the full profile has
 * been merged and the icon corrects itself.
 */
export function isEndUserHydrated(user: Partial<EdgeUser>): boolean {
    return !!(
        user.key ||
        user.firstName ||
        user.lastName ||
        user.displayName
    );
}
